"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEVICE_KEYS = void 0;
exports.deviceValues = deviceValues;
const finance_1 = require("./finance");
/**
 * The device provider's key space — the SPECIFIC hardware a document is about,
 * as opposed to the `equipment` provider's roll-up of everything on the order.
 *
 * Adding a key here and not producing it in `deviceValues` is a COMPILE ERROR
 * (that function `satisfies Record<DeviceKey, string | undefined>`).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * SINGULAR KEYS RESOLVE ONLY WHEN THERE IS EXACTLY ONE DEVICE.
 *
 * `manufacturer`, `bundle_name`, `bundle_contents`, `warranty_months`,
 * `unit_price`, `sale_mode`, `serial` and `terminal_id` are statements about
 * ONE piece of hardware. On an order carrying three different devices there is
 * no truthful answer, so they return `undefined` and the renderer throws
 * `MissingMergeFieldError` rather than picking the first line and asserting it.
 * A contract that names one model when three were sold is worse than one that
 * fails to render — the author is meant to reach for the equipment schedule.
 *
 * `model` IS THE EXCEPTION, by decision (2026-09-02). WHAT hardware an
 * agreement covers has a truthful multi-device answer — the list of them —
 * where a warranty term or a unit price does not, because those differ per
 * line and a joined string would read as one term. So `model` names the one
 * device on a single-device order and NAMES THEM ALL on a larger one. It stays
 * `undefined` only for an EMPTY order, which is a defect (an equipment
 * agreement covering no equipment) and is meant to fail loudly.
 *
 * `quantity` and `line_total` are aggregates and always resolve.
 */
exports.DEVICE_KEYS = [
    'model', 'manufacturer', 'bundle_name', 'bundle_contents', 'warranty_months',
    'quantity', 'unit_price', 'line_total', 'sale_mode', 'serial', 'terminal_id',
];
/**
 * How the line was paid for, in words a signer reads. Kept here rather than in
 * the UI so the contract and the order screen cannot phrase it differently.
 */
function saleModeText(mode, termMonths) {
    switch (mode) {
        case 'outright': return 'Purchased outright';
        case 'installment': return termMonths ? `Financed over ${termMonths} months` : 'Financed in instalments';
        case 'lease': return termMonths ? `Leased over ${termMonths} months` : 'Leased';
        case 'rental': return 'Rented monthly, no fixed term';
        case 'included': return 'Included at no charge';
        default: return undefined;
    }
}
/** "K10 Terminal, Cash Drawer, 2x Thermal Printer" — the kit's parts, in order. */
function componentsText(components) {
    if (!components || components.length === 0)
        return undefined;
    return components.map((c) => (c.quantity > 1 ? `${c.quantity}x ${c.name}` : c.name)).join(', ');
}
/**
 * "K10 POS Terminal, Cash Drawer, 2x Thermal Printer" — every model on the
 * order, in the order it was sold, quantities shown only where they exceed one.
 * Same shape as `componentsText` so a kit's parts and an order's devices are
 * never written two different ways in the same contract.
 *
 * A line with no model name is named by its BUNDLE — that line was sold as a
 * kit and the kit's name is what the customer bought. A line with neither is
 * skipped rather than printed as "Device": an unnamed entry in a list of what
 * an agreement covers is worse than a shorter list, and the empty result then
 * falls through to `undefined` and throws.
 */
function modelListText(lines) {
    const parts = lines
        .map((l) => ({ name: l.modelName || l.bundleName, quantity: l.quantity }))
        .filter((p) => !!p.name)
        .map((p) => (p.quantity > 1 ? `${p.quantity}x ${p.name}` : p.name));
    return parts.length ? parts.join(', ') : undefined;
}
function deviceValues(lines) {
    // The one-device case is the only one where a singular claim is true.
    const only = lines.length === 1 ? lines[0] : undefined;
    const totalUnits = lines.reduce((n, l) => n + l.quantity, 0);
    const lineTotalCents = lines.reduce((sum, l) => sum + (l.unitPriceCents ?? 0) * l.quantity, 0);
    return {
        // Single-device behaviour is byte-for-byte what it always was — that case
        // is also what `equipmentScheduleRows` calls, one line at a time, so every
        // already-frozen `equipment_lines_json` still serialises identically.
        model: lines.length === 1 ? only?.modelName : modelListText(lines),
        manufacturer: only?.manufacturer,
        bundle_name: only?.bundleName,
        bundle_contents: componentsText(only?.bundleComponents),
        warranty_months: typeof only?.warrantyMonths === 'number' ? String(only.warrantyMonths) : undefined,
        quantity: lines.length ? String(totalUnits) : undefined,
        unit_price: typeof only?.unitPriceCents === 'number' ? (0, finance_1.dollars)(only.unitPriceCents) : undefined,
        line_total: lines.length ? (0, finance_1.dollars)(lineTotalCents) : undefined,
        sale_mode: saleModeText(only?.saleMode, only?.termMonths),
        serial: only?.serial,
        terminal_id: only?.terminalId,
    };
}
//# sourceMappingURL=device.js.map